import {alias, createModelSchema, custom, deserialize, primitive, list, serialize, SKIP} from "serializr";
import {Cloneable} from "@/common/Cloneable";
import {Schema} from "@/common/Schema";

import {SearchKind} from "./SearchKind";
import {SearchFilter} from "./SearchFilter";
import {ItemKey} from "../item/core/ItemKey";
import {Point} from "../utils/Point";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {Tile} from "@/modules/redactor/models/utils/Tile";

export class SearchModel extends Cloneable {

    static schema() {
        return custom(
            (v) => {
                return v && !v.empty() ? serialize(SearchModel, v) : SKIP;
            },
            (v) => {
                return deserialize(SearchModel, v) || new SearchModel();
            }
        );
    }

    constructor(kind = null) {
        super();

        this._kind = kind;
        this._tile = null;
        this._items = [];
        this._filter = SearchFilter.NONE;
        this._offset = new Point();
        this._tutorial = null;
        this._searchLowestLevel = false;
        this._excludeSourcing = false;
    }

    get kind() {
        return this._kind;
    }

    set kind(value) {
        this._kind = value;
    }

    get tile() {
        return this._tile;
    }

    set tile(value) {
        this._tile = value;
    }

    get items() {
        return this._items;
    }

    set items(value) {
        this._items = value;
    }

    get filter() {
        return this._filter;
    }

    set filter(value) {
        this._filter = value;
    }

    get offset() {
        return this._offset;
    }

    set offset(value) {
        this._offset = value;
    }

    get tutorial() {
        return this._tutorial;
    }

    set tutorial(value) {
        this._tutorial = value;
    }

    get searchLowestLevel() {
        return this._searchLowestLevel;
    }

    set searchLowestLevel(value) {
        this._searchLowestLevel = value;
    }

    get excludeSourcing() {
        return this._excludeSourcing;
    }

    set excludeSourcing(value) {
        this._excludeSourcing = value;
    }

    valid() {
        if (!SearchKind.is(this.kind)) {
            return false;
        }

        if (SearchKind.SHOP.equal(this.kind) ||
            SearchKind.MAGNET.equal(this.kind) ||
            SearchKind.DYNAMIT.equal(this.kind)) {
            return true;
        } else if (SearchKind.CELL.equal(this.kind)) {

            if (!this.tile || !this.tile.valid()) {
                return false;
            }

        } else {

            if (!SearchFilter.is(this.filter)) {
                return false;
            }

            if (this.items && this.items.some(v => !v || !v.valid() || v.any())) {
                return false;
            }

            if (this.offset && !this.offset.valid()) {
                return false;
            }

            if (this.tutorial && !RedactorUtils.getTutorial(this.tutorial)) {
                return false;
            }

            return this.tutorial != null || (this.items && this.items.length > 0);
        }

        return true;
    }

    empty() {
        return !this.kind || this.kind.equal(SearchKind.NONE);
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new SearchModel());
        } else if (target instanceof SearchModel) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }

}

createModelSchema(SearchModel, {
    _kind: alias("kind", SearchKind.schema()),
    _tile: alias("tile", Tile.schema()),
    _items: alias("items", list(ItemKey.schema())),
    _filter: alias("filter", SearchFilter.schema()),
    _offset: alias("offset", Point.schema()),
    _tutorial: alias("tutorial_uid", Schema.primitive()),
    _searchLowestLevel: alias("search_lowest_level", primitive()),
    _excludeSourcing: alias("exclude_sourcing", Schema.primitive()),
});